import React from 'react';
import { Mail, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const StepDeveloperInfo: React.FC<Props> = ({ data, updateData }) => {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Tell us who will be responsible for installing the chat widget on your website. 
        We'll send them the embed code and installation instructions.
      </p>

      {/* Developer Email */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Developer/IT Contact Email <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="email"
            value={data.developerEmail}
            onChange={(e) => updateData({ developerEmail: e.target.value })}
            placeholder="developer@yourcompany.com"
            className="pl-10"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          This person will receive the embed code when your assistant is ready.
        </p>
      </div>

      {/* Install Scope */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          Installation Scope <span className="text-red-500">*</span>
        </label>
        <RadioGroup
          value={data.installScope}
          onValueChange={(value: 'site-wide' | 'specific-pages') => 
            updateData({ installScope: value })
          }
          className="space-y-3"
        >
          <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 cursor-pointer">
            <RadioGroupItem value="site-wide" id="site-wide" className="mt-0.5" />
            <Label htmlFor="site-wide" className="cursor-pointer flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Settings className="h-4 w-4 text-brand-teal" />
                <span className="font-medium">Site-wide</span>
              </div>
              <p className="text-sm text-muted-foreground">
                The chat widget will appear on all pages of your website.
              </p>
            </Label>
          </div>

          <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 cursor-pointer">
            <RadioGroupItem value="specific-pages" id="specific-pages" className="mt-0.5" />
            <Label htmlFor="specific-pages" className="cursor-pointer flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Settings className="h-4 w-4 text-brand-teal" />
                <span className="font-medium">Specific Pages Only</span>
              </div>
              <p className="text-sm text-muted-foreground">
                The chat widget will only appear on selected pages (e.g., contact, services).
              </p>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
        <p className="text-sm text-card-foreground">
          <strong>💡 Tip:</strong> Most clients choose site-wide installation. 
          If you select specific pages, we'll work with your developer to configure the targeting.
        </p>
      </div>
    </div>
  );
};

export default StepDeveloperInfo;
